import React, {useContext, useEffect, useState} from "react";
import S from "./GrowthGraph.styled";
import CardHeader from "../CardHeader";
import FundContext from "@/components/FundSingle/FundProvider";
import moment from 'moment';
import {fetchGrowthGraphData, renderGrowthChart} from "./growthGraphUtils";
import { createRoot } from 'react-dom/client';

export interface GrowthGraphProps {
    withSettings?: boolean; 
    growth10?: boolean;
}

const GrowthGraph: React.FC<GrowthGraphProps> = ({withSettings, growth10 = false}) => {
    const { securityId, fundName, currentTabIndex, hasInitCharts, fundRiskDetails } = useContext(FundContext);
    const [currentInterval, setCurrentInterval] = useState(5);
    const [customStartDate, setCustomStartDate] = useState('');
    const [customEndDate, setCustomEndDate] = useState('');
    const [loading, setLoading] = useState(false);

    const graphDateSettings = [
        { label: '1 m.', value: new Date(new Date().setMonth(new Date().getMonth() - 1)) },
        { label: '3 m.', value: new Date(new Date().setMonth(new Date().getMonth() - 3)) },
        { label: '6 m.', value: new Date(new Date().setMonth(new Date().getMonth() - 6)) },
        { label: '1 år', value: new Date(new Date().setFullYear(new Date().getFullYear() - 1)) },
        { label: '3 år', value: new Date(new Date().setFullYear(new Date().getFullYear() - 3)) },
        { label: '5 år', value: new Date(new Date().setFullYear(new Date().getFullYear() - 5)) },
        { label: '10 år', value: new Date(new Date().setFullYear(new Date().getFullYear() - 10)) },
        { label: 'Valfri tidsperiod', value: 'custom' },
    ];

    useEffect(() => {

        function initGrowthChart() {
            
            var startDate = graphDateSettings[currentInterval].value;
            var endDate = new Date();

            if (currentInterval == graphDateSettings.length - 1) {
                if (customStartDate && customEndDate) {
                    startDate = new Date(customStartDate);
                    endDate = new Date(customEndDate);
                }
                else return;
            }

            // Date ten years ago in DD-MM-YYYY format
            var dateTenYearsAgoString = moment(new Date(new Date().setFullYear(new Date().getFullYear() - 10))).format('YYYY-MM-DD');

            // if (growth10) {
            //     startDate = new Date(new Date().setFullYear(new Date().getFullYear() - 5));
            // }

            setLoading(true);
            Promise.all([
                fetchGrowthGraphData(securityId, dateTenYearsAgoString, endDate.toISOString()),
                fetchGrowthGraphData(securityId, startDate.toISOString(), endDate.toISOString()),
            ]).then(([growthDataTenYearsAgo, growthData]) => {
                setLoading(false);

                var values = growthData.map((item: any) => {
                    const growth = ((item.Value - growthData[0].Value) / growthData[0].Value) * 100;
                    return growth;
                });
                
                if (!growth10) {
                    values = growthData.map((item: any) => {
                        return item.Value;
                    });
                }

                // Combine the two datasets
                var dataSets = [
                    {
                        label: 'Utveckling',
                        data: values,
                        borderColor: '#001E63',
                    }
                ];

                var labels = growthData.map((item: any) => {
                    return item.EndDate;
                });
                
                var chartData = {
                    labels: labels,
                    datasets: dataSets
                };
                
                // Render the chart
                renderGrowthChart(chartData, 'growthGraphContainer', growth10, currentInterval < 3, fundName, growthData[0].Value);

                if (withSettings) {
                    // Fill in "growthPercentage-" + index with the growth percentage
                    graphDateSettings.forEach((setting, index) => {
                        const { value } = setting;

                        if (setting.value == 'custom') {
                            return;
                        }

                        const currentDate = new Date(growthDataTenYearsAgo[growthDataTenYearsAgo.length - 1].EndDate);
                        const settingDate = value;

                        // Use moment.js to format the dates
                        const currentDateFormatted = moment(currentDate).format('YYYY-MM-DD');
                        const settingDateFormatted = moment(settingDate).format('YYYY-MM-DD');

                        // Find the percentage value between firstDate and currentDate. We need to find the corresponding values in the graphData
                        const currentDateValue = growthDataTenYearsAgo.find((item: any) => {
                            var formattedItemDate = moment(item.EndDate).format('YYYY-MM-DD');
                            if (formattedItemDate.trim() == currentDateFormatted.trim()) {
                                return item;
                            }
                        });

                        var settingDateValue = growthDataTenYearsAgo.find((item: any) => {
                            var formattedItemDate = moment(item.EndDate).format('YYYY-MM-DD');
                            if (formattedItemDate.trim() == settingDateFormatted.trim()) {
                                return item;
                            }
                        });

                        if (!settingDateValue) {
                            // If we don't find the exact date, we need to find the closest date
                            var closestDate = growthDataTenYearsAgo.reduce(function(prev, curr) {
                                return (Math.abs(new Date(curr.EndDate) - settingDate) < Math.abs(new Date(prev.EndDate) - settingDate) ? curr : prev);
                            });

                            settingDateValue = closestDate;
                        }

                        if (currentDateValue && settingDateValue) {
                            var growth = ((currentDateValue.Value - settingDateValue.Value) / settingDateValue.Value) * 100;
                            var growthRounded = growth.toFixed(2);
                            if (growth > 0) {
                                growthRounded = '+' + growthRounded;
                            }
                            var elem = document.getElementById('growthPercentage-' + index);
                            if (elem) {
                                elem.innerHTML = growthRounded + ' %';
                            }
                        }
                    });
                }

            }).catch(error => {
                console.error('Error fetching data:', error);
            });
        }

        if (hasInitCharts) {
            initGrowthChart();
        } else {
            window.addEventListener('chartjsLoaded', () => {
                initGrowthChart();
            });
        }

    }, [currentTabIndex, currentInterval, customStartDate, customEndDate, fundRiskDetails]);

    useEffect(() => {
        // Loading
        if (loading) {
            var spinner = <S.SpinnerWrapper><S.Spinner /></S.SpinnerWrapper>;
            const container = document.getElementById('growthGraphContainer');
            const root = createRoot(container);
            root.render(<S.SpinnerWrapper><S.Spinner /></S.SpinnerWrapper>);
        }
    } , [loading]);

    return (
        <S.FondFactsWrapper>

            {growth10 && (
                <CardHeader title={"Tillväxt för 100 (SEK)"} subTitle={moment(new Date()).format('YYYY-MM-DD')} />
            )}

            {!growth10 && (
                <CardHeader title="Utveckling" subTitle={moment(new Date()).format('YYYY-MM-DD')}  />
            )}

            <div id="growthGraphContainer"></div>

            {withSettings && (
                <S.DateSettingsWrapper>
                    {graphDateSettings.map((setting, index) => function() {
                        const { label, value } = setting;
                        const isActive = currentInterval === index;

                        return (
                            <S.DateSetting key={index} isActive={isActive} onClick={() => setCurrentInterval(index)}>
                                {label}
                                <span id={"growthPercentage-" + index}></span>
                            </S.DateSetting>
                        );
                    }())}
                </S.DateSettingsWrapper>
            )}

            {currentInterval == graphDateSettings.length - 1 && (
                <S.CustomDateSettingsWrapper>
                    <S.CustomDateSettingInput>
                        <label htmlFor="graphStartDate">Från datum</label>
                        <input type="date" name="graphStartDate" onChange={(e) => setCustomStartDate(e.target.value)} />
                    </S.CustomDateSettingInput>
                    <S.CustomDateSettingInput>
                        <label htmlFor="graphEndDate">Till datum</label>
                        <input type="date" name="graphEndDate" onChange={(e) => setCustomEndDate(e.target.value)} />
                    </S.CustomDateSettingInput>
                </S.CustomDateSettingsWrapper>
            )}

        </S.FondFactsWrapper>
    );
};

export default GrowthGraph;
